import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { schedulingAPI, tasksAPI, meetingsAPIExtended, contactsAPI } from '../api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { format, addDays, parseISO, isToday, isTomorrow, startOfWeek, endOfWeek, differenceInDays } from 'date-fns';
import {
  Sparkles, Calendar, Clock, Users, TrendingUp, Brain,
  ChevronRight, RefreshCw, Zap, Target, CheckCircle2,
  AlertCircle, Lightbulb, CalendarPlus, ExternalLink,
  Globe, Building, User, Timer, BarChart3, Settings
} from 'lucide-react';

// Suggestion Card Component
const SuggestionCard = ({ suggestion, onAccept, onDismiss }) => {
  const getIcon = () => {
    switch (suggestion.type) {
      case 'follow_up': return <Target className="h-4 w-4" />;
      case 'meeting': return <Calendar className="h-4 w-4" />;
      case 'task': return <CheckCircle2 className="h-4 w-4" />;
      case 'recurring': return <RefreshCw className="h-4 w-4" />;
      default: return <Lightbulb className="h-4 w-4" />;
    }
  };

  const getPriorityColor = () => {
    switch (suggestion.priority) {
      case 'high': return 'border-red-200 bg-red-50';
      case 'medium': return 'border-amber-200 bg-amber-50';
      default: return 'border-blue-200 bg-blue-50';
    }
  };

  return (
    <Card className={`${getPriorityColor()} transition-all hover:shadow-md`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${
            suggestion.priority === 'high' ? 'bg-red-100 text-red-600' :
            suggestion.priority === 'medium' ? 'bg-amber-100 text-amber-600' :
            'bg-blue-100 text-blue-600'
          }`}>
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm">{suggestion.title}</h4>
            <p className="text-xs text-muted-foreground mt-1">{suggestion.description}</p>
            
            {suggestion.suggested_time && (
              <div className="flex items-center gap-2 mt-2 text-xs">
                <Clock className="h-3 w-3" />
                <span>{suggestion.suggested_time}</span>
              </div>
            )}
            
            {suggestion.contact_name && (
              <div className="flex items-center gap-2 mt-1 text-xs">
                <User className="h-3 w-3" />
                <span>{suggestion.contact_name}</span>
              </div>
            )}
            
            {suggestion.reason && (
              <div className="mt-2 p-2 bg-white/50 rounded text-xs">
                <span className="font-medium">Why: </span>
                {suggestion.reason}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="flex-1" onClick={() => onAccept(suggestion)}>
            <CalendarPlus className="h-3 w-3 mr-1" />
            Schedule
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDismiss(suggestion.id)}>
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Availability Slot Component
const AvailabilitySlot = ({ slot, onSelect }) => {
  return (
    <button
      onClick={() => onSelect(slot)}
      className="w-full p-3 text-left border rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-sm">{slot.label}</p>
          <p className="text-xs text-muted-foreground">{slot.time_range}</p>
        </div>
        <Badge variant={slot.score >= 80 ? 'default' : 'secondary'} className="text-xs">
          {slot.score}% match
        </Badge>
      </div>
      {slot.reason && (
        <p className="text-xs text-muted-foreground mt-1">{slot.reason}</p>
      )}
    </button>
  );
};

// Pattern Insight Component
const PatternInsight = ({ insight }) => {
  return (
    <div className="p-3 border rounded-lg">
      <div className="flex items-start gap-2">
        <BarChart3 className="h-4 w-4 text-purple-500 mt-0.5" />
        <div>
          <p className="text-sm font-medium">{insight.title}</p>
          <p className="text-xs text-muted-foreground mt-1">{insight.description}</p>
          {insight.recommendation && (
            <p className="text-xs text-purple-600 mt-2 font-medium">
              💡 {insight.recommendation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Smart Scheduling Assistant Component
export const SmartSchedulingAssistant = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('suggestions');
  
  // Data states
  const [suggestions, setSuggestions] = useState([]);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [contacts, setContacts] = useState([]);
  
  // Microsoft Calendar connection state
  const [msCalendarConnected, setMsCalendarConnected] = useState(false);
  const [msCalendarLoading, setMsCalendarLoading] = useState(false);
  
  // Schedule dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '09:00',
    duration: '30',
    contact_id: ''
  });

  // Fetch scheduling data when panel opens
  const fetchSchedulingData = useCallback(async () => {
    if (!isOpen) return;
    
    setLoading(true);
    try {
      // Fetch AI-powered suggestions
      const [suggestionsRes, patternsRes] = await Promise.all([
        schedulingAPI.getSuggestions(),
        schedulingAPI.getPatterns()
      ]);
      
      setSuggestions(suggestionsRes.data?.suggestions || []);
      setPatterns(patternsRes.data?.patterns || []);
      
      // Check Microsoft Calendar connection status
      try {
        const msStatusRes = await schedulingAPI.getMicrosoftCalendarStatus();
        setMsCalendarConnected(msStatusRes.data?.connected || false);
      } catch {
        setMsCalendarConnected(false);
      }
      
      // Fetch contacts for scheduling
      const contactsRes = await contactsAPI.getAll();
      setContacts(contactsRes.data?.contacts || contactsRes.data || []);
      
    } catch (error) {
      console.error('Failed to fetch scheduling data:', error);
      toast.error('Failed to load scheduling suggestions');
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    fetchSchedulingData();
  }, [fetchSchedulingData]);

  // Handle accepting a suggestion
  const handleAcceptSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setScheduleForm({
      date: suggestion.suggested_date || format(new Date(), 'yyyy-MM-dd'),
      time: suggestion.suggested_time_slot || '09:00',
      duration: suggestion.duration || '30',
      contact_id: suggestion.contact_id || ''
    });
    setScheduleDialogOpen(true);
  };

  // Handle dismissing a suggestion
  const handleDismissSuggestion = async (suggestionId) => {
    try {
      await schedulingAPI.dismissSuggestion(suggestionId);
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      toast.success('Suggestion dismissed');
    } catch (error) {
      toast.error('Failed to dismiss suggestion');
    }
  };

  // Handle creating a scheduled task/meeting
  const handleScheduleSubmit = async () => {
    try {
      setLoading(true);
      
      const scheduleData = {
        title: selectedSuggestion?.title || 'Scheduled Task',
        description: selectedSuggestion?.description || '',
        due_date: `${scheduleForm.date}T${scheduleForm.time}:00`,
        duration_minutes: parseInt(scheduleForm.duration),
        contact_id: scheduleForm.contact_id || null,
        type: selectedSuggestion?.type || 'task',
        source: 'smart_scheduling_assistant'
      };

      // Create task or meeting based on type
      if (selectedSuggestion?.type === 'meeting') {
        await meetingsAPIExtended.create({
          title: scheduleData.title,
          scheduled_datetime: scheduleData.due_date,
          duration_minutes: scheduleData.duration_minutes,
          contact_id: scheduleData.contact_id
        });
      } else {
        await tasksAPI.create({
          title: scheduleData.title,
          description: scheduleData.description,
          due_date: scheduleData.due_date,
          priority: selectedSuggestion?.priority || 'medium',
          contact_id: scheduleData.contact_id,
          related_to_type: scheduleData.contact_id ? 'contact' : null,
          related_to_id: scheduleData.contact_id
        });
      }

      toast.success('Successfully scheduled!');
      setScheduleDialogOpen(false);
      setSelectedSuggestion(null);
      
      // Remove the suggestion from list
      if (selectedSuggestion?.id) {
        setSuggestions(prev => prev.filter(s => s.id !== selectedSuggestion.id));
      }
      
      // Refresh data
      fetchSchedulingData();
      
    } catch (error) {
      console.error('Failed to schedule:', error);
      toast.error('Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  // Check availability for a specific time
  const handleCheckAvailability = async (date) => {
    try {
      const res = await schedulingAPI.checkAvailability(date);
      setAvailabilitySlots(res.data?.slots || []);
    } catch (error) {
      console.error('Failed to check availability:', error);
    }
  };

  // Connect Microsoft Calendar
  const handleConnectMicrosoftCalendar = async () => {
    setMsCalendarLoading(true);
    try {
      const res = await schedulingAPI.initMicrosoftAuth();
      if (res.data?.auth_url) {
        // Open Microsoft auth in new window
        window.open(res.data.auth_url, '_blank', 'width=600,height=700');
        toast.info('Complete authentication in the popup window');
      }
    } catch (error) {
      toast.error('Failed to initiate Microsoft Calendar connection');
    } finally {
      setMsCalendarLoading(false);
    }
  };

  return (
    <>
      {/* Floating Trigger Button */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon"
            className="relative group"
            data-testid="smart-scheduling-btn"
          >
            <Sparkles className="h-5 w-5 text-purple-500 group-hover:text-purple-600" />
            {suggestions.length > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center">
                {suggestions.length > 9 ? '9+' : suggestions.length}
              </span>
            )}
          </Button>
        </SheetTrigger>
        
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              Smart Scheduling Assistant
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-4">
            {/* Microsoft Calendar Connection Status */}
            <Card className={`mb-4 ${msCalendarConnected ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className={`h-4 w-4 ${msCalendarConnected ? 'text-green-600' : 'text-amber-600'}`} />
                  <span className="text-sm font-medium">
                    {msCalendarConnected ? 'Microsoft Calendar Connected' : 'Connect Microsoft Calendar'}
                  </span>
                </div>
                {!msCalendarConnected && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleConnectMicrosoftCalendar}
                    disabled={msCalendarLoading}
                  >
                    {msCalendarLoading ? (
                      <RefreshCw className="h-3 w-3 animate-spin" />
                    ) : (
                      'Connect'
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="suggestions" className="text-xs">
                  <Lightbulb className="h-3 w-3 mr-1" />
                  Suggestions
                </TabsTrigger>
                <TabsTrigger value="availability" className="text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  Availability
                </TabsTrigger>
                <TabsTrigger value="patterns" className="text-xs">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Patterns
                </TabsTrigger>
              </TabsList>

              {/* Suggestions Tab */}
              <TabsContent value="suggestions" className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-muted-foreground">
                    AI-powered scheduling recommendations
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={fetchSchedulingData}
                    disabled={loading}
                  >
                    <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                
                <ScrollArea className="h-[400px] pr-2">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin text-purple-500" />
                    </div>
                  ) : suggestions.length > 0 ? (
                    <div className="space-y-3">
                      {suggestions.map((suggestion, idx) => (
                        <SuggestionCard
                          key={suggestion.id || idx}
                          suggestion={suggestion}
                          onAccept={handleAcceptSuggestion}
                          onDismiss={handleDismissSuggestion}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Sparkles className="h-10 w-10 mx-auto text-purple-200 mb-3" />
                      <p className="text-sm text-muted-foreground">
                        No scheduling suggestions right now
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Check back later or add more tasks
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              {/* Availability Tab */}
              <TabsContent value="availability" className="mt-4">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm">Check availability for</Label>
                    <Input
                      type="date"
                      className="mt-1"
                      defaultValue={format(new Date(), 'yyyy-MM-dd')}
                      onChange={(e) => handleCheckAvailability(e.target.value)}
                    />
                  </div>
                  
                  <ScrollArea className="h-[350px] pr-2">
                    {availabilitySlots.length > 0 ? (
                      <div className="space-y-2">
                        {availabilitySlots.map((slot, idx) => (
                          <AvailabilitySlot
                            key={idx}
                            slot={slot}
                            onSelect={(s) => {
                              setScheduleForm(prev => ({
                                ...prev,
                                date: s.date,
                                time: s.start_time
                              }));
                              toast.success(`Selected ${s.label}`);
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="h-10 w-10 mx-auto text-slate-200 mb-3" />
                        <p className="text-sm text-muted-foreground">
                          Select a date to check availability
                        </p>
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </TabsContent>

              {/* Patterns Tab */}
              <TabsContent value="patterns" className="mt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-muted-foreground">
                    Your productivity patterns
                  </p>
                </div>
                
                <ScrollArea className="h-[400px] pr-2">
                  {patterns.length > 0 ? (
                    <div className="space-y-3">
                      {patterns.map((pattern, idx) => (
                        <PatternInsight key={idx} insight={pattern} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <TrendingUp className="h-10 w-10 mx-auto text-slate-200 mb-3" />
                      <p className="text-sm text-muted-foreground">
                        Not enough data to show patterns yet
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Complete more tasks to see insights
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-purple-500" />
              Schedule {selectedSuggestion?.type === 'meeting' ? 'Meeting' : 'Task'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title</Label>
              <Input
                value={selectedSuggestion?.title || ''}
                onChange={(e) => setSelectedSuggestion(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter title..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={scheduleForm.date}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={scheduleForm.time}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <Label>Duration</Label>
              <Select
                value={scheduleForm.duration}
                onValueChange={(value) => setScheduleForm(prev => ({ ...prev, duration: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Related Contact (Optional)</Label>
              <Select
                value={scheduleForm.contact_id}
                onValueChange={(value) => setScheduleForm(prev => ({ ...prev, contact_id: value === 'none' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a contact..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contact</SelectItem>
                  {contacts.slice(0, 20).map(contact => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.name || `${contact.first_name} ${contact.last_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleScheduleSubmit} disabled={loading}>
              {loading ? 'Scheduling...' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SmartSchedulingAssistant;
